import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardHeader,
  CardContent,
  TextField,
  IconButton,
  Button,
  Stack,
  Divider,
  Dialog,
  DialogContent
} from '@mui/material';
import {
  PhoneIphone,
  DesktopMac,
  Save,
  Link as LinkIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import axios from 'axios';
import { appConfig } from '../../config/app';
import { useApp } from '../../context/AppContext';
import ImageUpload from '../../components/common/ImageUpload';

const AppWebsiteBanners = () => {
  const { actions } = useApp();
  const [mobileBanners, setMobileBanners] = useState([]);
  const [desktopBanners, setDesktopBanners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null); // { type: 'mobile' | 'desktop', index: number }

  const loadBanners = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${appConfig.api.baseURL}/banners`);
      if (res.data?.success) {
        setMobileBanners(res.data.data.mobileBanners || []);
        setDesktopBanners(res.data.data.desktopBanners || []);
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to load banners', 'error');
      }
    } catch (error) {
      console.error('Error loading banners:', error);
      actions.showSnackbar('Failed to load banners', 'error');
    } finally {
      setLoading(false);
    }
  }, [actions]);

  useEffect(() => {
    loadBanners();
  }, [loadBanners]);

  const syncUrlsToBanners = (currentBanners, urls) => {
    const next = [];
    for (let i = 0; i < urls.length; i += 1) {
      const existing = currentBanners[i];
      next.push({
        url: urls[i],
        route: existing?.route || ''
      });
    }
    return next;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        mobileBanners,
        desktopBanners
      };
      const res = await axios.post(`${appConfig.api.baseURL}/banners`, payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (res.data?.success) {
        actions.showSnackbar('Banners saved successfully', 'success');
      } else {
        actions.showSnackbar(res.data?.message || 'Failed to save banners', 'error');
      }
    } catch (error) {
      console.error('Error saving banners:', error);
      actions.showSnackbar('Failed to save banners', 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderBannerList = (banners, setBanners, type) => (
    <Stack spacing={2} mt={2}>
      {banners.map((banner, index) => (
        <Box
          key={banner.url || index}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: 3,
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper'
          }}
        >
          <Box
            sx={{
              width: 260,
              height: 140,
              borderRadius: 1,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'grey.100',
              flexShrink: 0,
              cursor: banner.url ? 'pointer' : 'default'
            }}
            onClick={() => {
              if (banner.url) {
                setPreviewImage(banner.url);
              }
            }}
          >
            {banner.url && (
              <Box
                component="img"
                src={banner.url}
                alt={`Banner ${index + 1}`}
                sx={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  display: 'block'
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <TextField
              size="small"
              fullWidth
              value={banner.route || ''}
              onChange={(e) => {
                const next = [...banners];
                next[index] = { ...next[index], route: e.target.value };
                setBanners(next);
              }}
              InputProps={{
                startAdornment: <LinkIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
            <IconButton
              color="error"
              onClick={() => {
                setDeleteTarget({ type, index });
                setDeleteDialogOpen(true);
              }}
              aria-label="Delete banner"
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
      ))}
      {banners.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No banners uploaded yet.
        </Typography>
      )}
    </Stack>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            App &amp; Website Banners
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage mobile and desktop banners and their click routes.
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <IconButton onClick={loadBanners} disabled={loading}>
            <RefreshIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<Save />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </Stack>
      </Box>

      {/* Mobile section */}
      <Card>
        <CardHeader
          avatar={<PhoneIphone color="primary" />}
          title="Mobile Banners"
          subheader="Optimized for mobile devices"
        />
        <CardContent>
          <ImageUpload
            images={mobileBanners.map((b) => b.url)}
            onImagesChange={(urls) => setMobileBanners((prev) => syncUrlsToBanners(prev, urls))}
            uploadPath="banners/mobile"
            maxImages={10}
            label="Upload Mobile Banners"
            helperText="Recommended aspect ratio for mobile banners"
            showPreviews={false}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            On click routes
          </Typography>
          {renderBannerList(mobileBanners, setMobileBanners, 'mobile')}
        </CardContent>
      </Card>

      {/* Desktop section, directly below mobile */}
      <Card sx={{ mt: 4 }}>
        <CardHeader
          avatar={<DesktopMac color="primary" />}
          title="Desktop Banners"
          subheader="Optimized for desktop and web"
        />
        <CardContent>
          <ImageUpload
            images={desktopBanners.map((b) => b.url)}
            onImagesChange={(urls) => setDesktopBanners((prev) => syncUrlsToBanners(prev, urls))}
            uploadPath="banners/desktop"
            maxImages={10}
            label="Upload Desktop Banners"
            helperText="Recommended aspect ratio for desktop banners"
            showPreviews={false}
          />
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            On click routes
          </Typography>
          {renderBannerList(desktopBanners, setDesktopBanners, 'desktop')}
        </CardContent>
      </Card>

      {/* Image preview dialog */}
      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none'
          }
        }}
      >
        <DialogContent sx={{ p: 0, position: 'relative' }}>
          <IconButton
            onClick={() => setPreviewImage(null)}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: 'white',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.7)'
              },
              zIndex: 1
            }}
          >
            <CloseIcon />
          </IconButton>
          {previewImage && (
            <Box
              component="img"
              src={previewImage}
              alt="Banner preview"
              sx={{
                width: '100%',
                height: 'auto',
                display: 'block',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Remove banner?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This will remove the selected banner from the list. You need to save changes to persist this.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
            <Button
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeleteTarget(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                if (deleteTarget) {
                  if (deleteTarget.type === 'mobile') {
                    setMobileBanners(prev => prev.filter((_, i) => i !== deleteTarget.index));
                  } else if (deleteTarget.type === 'desktop') {
                    setDesktopBanners(prev => prev.filter((_, i) => i !== deleteTarget.index));
                  }
                }
                setDeleteDialogOpen(false);
                setDeleteTarget(null);
              }}
            >
              Delete
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AppWebsiteBanners;

